import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SensitiveField from '@/components/ui/SensitiveField';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  Loader2,
  CheckCircle2,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '@/i18n';

const ProfilePage = () => {
  const t = useT();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    nationality: '',
    email_display: '',
    phone_masked: false,
    email_masked: false,
    address: {
      street: '',
      city: '',
      postal_code: '',
      country: ''
    }
  });

  // Sensitive fields: once verified, show editable input
  const [unlockedFields, setUnlockedFields] = useState({});

  const isOwner = user?.role === 'OWNER';
  const isCustomer = user?.role === 'CUSTOMER';

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      const userData = response.data;
      setProfile({
        name: userData.name || '',
        email: userData.email || '',
        email_display: userData.email_display || userData.email || '',
        phone: userData.phone || '',
        nationality: userData.nationality || '',
        phone_masked: userData.phone_masked || false,
        email_masked: userData.email_masked || false,
        address: userData.address || {
          street: '',
          city: '',
          postal_code: '',
          country: ''
        }
      });
    } catch (error) {
      toast.error(t('profile_page.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldVerified = (fieldKey, actualValue) => {
    setUnlockedFields(prev => ({ ...prev, [fieldKey]: actualValue }));
    toast.success(t('profile_page.field_unlocked', { field: fieldKey }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        name: profile.name,
        nationality: profile.nationality,
        address: profile.address
      };

      // Include phone only if it's been unlocked (verified)
      if (unlockedFields.phone !== undefined) {
        updateData.phone = unlockedFields.phone;
      } else if (!profile.phone_masked) {
        updateData.phone = profile.phone;
      }

      await axios.put(`${API}/users/me`, updateData);
      toast.success(t('profile_page.updated'));
      setUnlockedFields({});
      fetchProfile();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile_page.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#194a50]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">{t('profile.title')}</h1>
        <p className="text-[#194a50]/70 mt-1">{t('profile_page.subtitle')}</p>
        {isOwner && (
          <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit">
            <ShieldCheck className="w-3.5 h-3.5" />
            {t('profile_page.masked_notice')}
          </div>
        )}
      </div>

      <form onSubmit={handleSave}>
        {/* Basic Information */}
        <Card className="shadow-sm border-[#194a50]/10 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-[#194a50]" />
              {t('profile_page.contact_info')}
            </CardTitle>
            <CardDescription>
              {isOwner
                ? t('profile_page.contact_desc_owner')
                : t('profile_page.contact_desc_customer')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name - always editable */}
            <div className="space-y-2">
              <Label>{t('profile_page.full_name')}</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder={t('profile_page.full_name_placeholder')}
                data-testid="profile-name-input"
              />
            </div>

            {/* Nationality */}
            <div className="space-y-2">
              <Label>{t('profile.nationality')}</Label>
              <Input
                value={profile.nationality}
                onChange={(e) => setProfile({ ...profile, nationality: e.target.value })}
                placeholder={t('profile_page.nationality_placeholder')}
                data-testid="profile-nationality-input"
              />
            </div>

            {/* Email */}
            {isOwner && profile.email_masked ? (
              <SensitiveField
                label={t('profile.email')}
                maskedValue={profile.email_display}
                fieldKey="email"
                icon={Mail}
                onVerified={(val) => handleFieldVerified('email', val)}
              />
            ) : (
              <div className="space-y-2">
                <Label>{t('profile.email')}</Label>
                <div className="relative">
                  <Input
                    type="email"
                    value={profile.email}
                    disabled
                    className="bg-gray-50 text-gray-500"
                    data-testid="profile-email-input"
                  />
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-[#194a50]/50 mt-1">{t('profile_page.email_locked')}</p>
              </div>
            )}

            {/* Phone */}
            {isOwner && profile.phone_masked ? (
              unlockedFields.phone !== undefined ? (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {t('profile_page.phone_number')}
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  </Label>
                  <div className="relative">
                    <Input
                      value={unlockedFields.phone}
                      onChange={(e) => setUnlockedFields(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+34 600 000 000"
                    />
                    <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  <p className="text-xs text-green-600">{t('profile_page.phone_verified')}</p>
                </div>
              ) : (
                <SensitiveField
                  label={t('profile_page.phone_number')}
                  maskedValue={profile.phone}
                  fieldKey="phone"
                  icon={Phone}
                  onVerified={(val) => handleFieldVerified('phone', val)}
                />
              )
            ) : (
              <div className="space-y-2">
                <Label>{t('profile_page.phone_number')}</Label>
                <div className="relative">
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="+34 600 000 000"
                    data-testid="profile-phone-input"
                  />
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="shadow-sm border-[#194a50]/10 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#194a50]" />
              {t('profile.address')}
            </CardTitle>
            <CardDescription>{t('profile_page.address_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{t('profile_page.street_address')}</Label>
                <Input
                  value={profile.address.street}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, street: e.target.value }
                  })}
                  placeholder="123 Main Street"
                  data-testid="profile-street-input"
                />
              </div>
              <div>
                <Label>{t('profile_page.city')}</Label>
                <Input
                  value={profile.address.city}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, city: e.target.value }
                  })}
                  placeholder="Barcelona"
                  data-testid="profile-city-input"
                />
              </div>
              <div>
                <Label>{t('profile_page.postal_code')}</Label>
                <Input
                  value={profile.address.postal_code}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, postal_code: e.target.value }
                  })}
                  placeholder="08001"
                  data-testid="profile-postal-input"
                />
              </div>
              <div className="col-span-2">
                <Label>{t('profile_page.country')}</Label>
                <Input
                  value={profile.address.country}
                  onChange={(e) => setProfile({
                    ...profile,
                    address: { ...profile.address, country: e.target.value }
                  })}
                  placeholder="Spain"
                  data-testid="profile-country-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Role Info */}
        <Card className="shadow-sm border-[#194a50]/10 mb-6 bg-gray-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#194a50]/70">{t('profile_page.account_type')}</p>
                <p className="font-semibold text-[#0c2431]">
                  {isOwner ? t('profile_page.role_owner') : isCustomer ? t('profile_page.role_customer') : user?.role}
                </p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          type="submit"
          disabled={saving}
          className="w-full bg-[#194a50] hover:bg-[#10555e]"
          data-testid="save-profile-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('common.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('profile_page.save_changes')}
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default ProfilePage;
